// Shared formatting utilities for consistent number display across the app

/**
 * Format balance with appropriate decimal places
 * Removes insignificant trailing zeros and handles very small numbers
 */
export const formatBalance = (balance) => {
  const numBalance = Number(balance);
  if (numBalance === 0) return '0';
  if (numBalance < 0.000001) {
    return numBalance.toPrecision(3);
  }
  if (numBalance >= 1) {
    const intPart = Math.floor(numBalance);
    const fracPart = Math.abs(numBalance - intPart);
    // Show decimals only if fractional part is at least 0.01 (1%) of integer part
    if (fracPart >= intPart * 0.01) {
      // Show up to 6 decimals, trim trailing zeros
      let formatted = numBalance.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      });
      formatted = formatted.replace(/(\.\d*?[1-9])0+$/,'$1').replace(/\.0+$/,'').replace(/\.$/, '');
      return formatted;
    } else {
      // Show only integer part
      return intPart.toLocaleString();
    }
  }
  // For numbers < 1, show up to 6 decimals, trim trailing zeros
  let formatted = numBalance.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
  formatted = formatted.replace(/(\.\d*?[1-9])0+$/,'$1').replace(/\.0+$/,'').replace(/\.$/, '');
  return formatted;
};

/**
 * Format price with appropriate decimal places based on value size
 */
export const formatPrice = (price, formatCurrency) => {
  const numPrice = Number(price);
  if (numPrice > 0 && numPrice < 0.01) {
    return `$${numPrice.toPrecision(4)}`;
  }
  return formatCurrency(numPrice);
};

/**
 * Smart number formatting that shows significant digits only
 * For numbers >= 1: shows 2 decimal places
 * For numbers < 1: shows up to 2 significant digits after decimal
 */
export const smartFormatNumber = (val) => {
  if (typeof val !== 'number') val = Number(val) || 0;
  if (val >= 1) {
    return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    const str = val.toString();
    if (!str.includes('.')) return str;
    const [, decimals] = str.split('.');
    let nonZeroCount = 0;
    let cutoff = 0;
    for (let i = 0; i < decimals.length; i++) {
      if (decimals[i] !== '0') nonZeroCount++;
      if (nonZeroCount === 2) {
        cutoff = i + 1;
        break;
      }
    }
    cutoff = cutoff || decimals.length;
    const formatted = val.toFixed(cutoff);
    // Add comma separators for the whole number part
    const [whole, decimal] = formatted.split('.');
    const wholeWithCommas = Number(whole).toLocaleString();
    return decimal ? `${wholeWithCommas}.${decimal}` : wholeWithCommas;
  }
};

/**
 * Format currency with appropriate decimal places based on value size
 */
export const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '$0.00';
  const numValue = Number(value);
  
  // For very large numbers (≥ 1000), show no decimals
  if (numValue >= 1000) {
    return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
  
  // For medium numbers (100-999), show no decimals
  if (numValue >= 100) {
    return `$${numValue.toFixed(0)}`;
  }
  
  // For small numbers (1-99), show 2 decimals
  if (numValue >= 1) {
    return `$${numValue.toFixed(2)}`;
  }
  
  // For very small numbers (0.01-0.99), show 4 decimals
  if (numValue >= 0.01) {
    return `$${numValue.toFixed(4)}`;
  }
  
  // For tiny numbers (< 0.01), show up to 8 decimals but trim trailing zeros
  const formatted = numValue.toFixed(8);
  const trimmed = formatted.replace(/\.?0+$/, ''); // Remove trailing zeros
  return `$${trimmed}`;
};

/**
 * Format trigger price for exit strategy steps
 */
export const formatTriggerPrice = (value) => {
  if (!value || isNaN(value)) return '';
  const numValue = Number(value);
  if (numValue >= 1) {
    return numValue.toFixed(2);
  }
  return numValue.toFixed(4);
};

/**
 * Format large numbers with K, M, B suffixes
 */
export const formatNumber = (num) => {
  if (!num || typeof num !== 'number') return 'N/A';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toLocaleString();
};

/**
 * Format percentage with sign
 */
export const formatPercentage = (value) => {
  if (!value || typeof value !== 'number') return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}; 